import { FunctionComponent, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ROUTE_CONFIG } from "@/routes/config";
import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import {
	useConfirmIcpMutation,
	useLazyGetListRecommendationsQuery,
	useLazyGetOnboardingIcpQuery,
	useSubmitIcpHintMutation,
} from "@/pages/Private/redux/onboardingIcp/onboardingIcp.api";

import { IcpInfo, ListRecommendations } from "../schema/icp";
import { ChatShell } from "../components/chat/ChatShell";
import { RecommendedListsMessage } from "../components/chat/RecommendedListsMessage";
import { useChatScript } from "../components/chat/useChatScript";
import { useCustomerSeedsStep } from "../components/chat/useCustomerSeedsStep";
import { useIcpConfirmStep } from "../components/chat/useIcpConfirmStep";
import { useOnboardingListsPrewarm } from "../components/lists/useOnboardingListsPrewarm";

import { pollIcpUntilReady } from "./pollIcpUntilReady";
import { DEFAULT_TIMING, OnboardingChatTiming, sleep } from "./onboardingChatTiming";

export type { OnboardingChatTiming };

/**
 * Step 3 of the pre-checkout onboarding (PREC-3653): a scripted chat that
 * presents the AI-generated ICP guess, folds free-text corrections back into
 * it, and hands over to the list-building step once confirmed.
 *
 * Rich branch: the guess is ready quickly (company LinkedIn/website known).
 * Thin branch: we ask for a one-liner about the user's best customers — the
 * answer seeds the generation and covers its real latency.
 */
export const OnboardingChat: FunctionComponent<{ timing?: OnboardingChatTiming }> = ({
	timing,
}) => {
	const { richWaitMs, hintWaitMs, pollIntervalMs, beatMs, recommendationWaitMs } = {
		...DEFAULT_TIMING,
		...timing,
	};
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, string>) =>
		t(`onboarding.chat.${key}`, options);

	const navigate = useNavigate();
	const { user } = useAppSelector(authSelector);
	const companyName = user?.company?.name ?? "";

	const [fetchIcp] = useLazyGetOnboardingIcpQuery();
	const [submitHint] = useSubmitIcpHintMutation();
	const [confirmIcp] = useConfirmIcpMutation();
	const [fetchRecommendations] = useLazyGetListRecommendationsQuery();
	const prewarmLists = useOnboardingListsPrewarm();

	const chat = useChatScript();
	const {
		messages,
		isTyping,
		setIsTyping,
		inputMode,
		setInputMode,
		inputValue,
		setInputValue,
		scrollRef,
		addAi,
		addUser,
		clearChips,
	} = chat;

	const cancelledRef = useRef(false);
	const bootedRef = useRef(false);
	/** The thin-branch opener answer — reused to skip re-asking for customers. */
	const thinHintRef = useRef<string | null>(null);

	/**
	 * The seeds question is asked once. The targeting can be corrected after it
	 * has been answered (PREC-3904), and re-asking for reference customers the
	 * user already gave reads as the chat having lost the thread.
	 */
	const seedsDoneRef = useRef(false);

	const seedsStep = useCustomerSeedsStep({
		chat,
		ts,
		isCancelled: () => cancelledRef.current,
		onDone: () => {
			seedsDoneRef.current = true;
			void showBoth();
		},
	});

	const confirmStep = useIcpConfirmStep({
		chat,
		ts,
		beatMs,
		isCancelled: () => cancelledRef.current,
		onSettled: () => {
			if (seedsDoneRef.current) {
				void showBoth();

				return;
			}

			askCustomers();
		},
	});

	const pollUntilReady = (maxWaitMs: number): Promise<IcpInfo | null> =>
		pollIcpUntilReady({
			fetchIcp: () => fetchIcp().unwrap(),
			maxWaitMs,
			pollIntervalMs,
			isCancelled: () => cancelledRef.current,
		});

	/**
	 * The list recommendation is a live AI call, so it is bounded: past the
	 * deadline (or on failure) the hand-over message pitches generically instead
	 * of leaving the chat hanging. It also warms the cache the picker reads.
	 */
	const loadRecommendations = async (): Promise<ListRecommendations | null> => {
		try {
			return await Promise.race([
				fetchRecommendations(undefined, true).unwrap(),
				sleep(recommendationWaitMs).then(() => null),
			]);
		} catch (err) {
			console.error(err);

			return null;
		}
	};

	/**
	 * Hands over to the lists step by previewing the two lists that step will
	 * recommend — the user should recognise them again on the next screen.
	 */
	const showBoth = async () => {
		setIsTyping(true);

		const recommendations = await loadRecommendations();

		if (cancelledRef.current) {
			return;
		}

		setIsTyping(false);
		addAi(<RecommendedListsMessage recommendations={recommendations} />, [
			{ label: ts("chipStart"), onClick: handleStart },
		]);
	};

	/**
	 * Asks for a few reference customers — the seeds of the lookalike list. In
	 * the thin branch the opener answer often already names a company, so it is
	 * run through the same extraction first and the question is skipped.
	 */
	const askCustomers = async () => {
		if (await seedsStep.tryReuseFromHint(thinHintRef.current)) {
			return;
		}

		seedsStep.askCustomers();
	};

	const handleStart = async () => {
		clearChips();
		addUser(ts("chipStart"));
		setIsTyping(true);

		try {
			await confirmIcp().unwrap();
		} catch (err) {
			console.error(err);
		}

		// The lists step blocks its rows on these lookups — start them during the
		// closing beat so it can render prefilled right away.
		prewarmLists();

		if (cancelledRef.current) {
			return;
		}

		setIsTyping(false);
		addAi(<>{ts("startAck")}</>);
		await sleep(beatMs);

		if (!cancelledRef.current) {
			navigate(ROUTE_CONFIG.ONBOARDING_LISTS);
		}
	};

	/**
	 * The hint endpoint generates the guess inline and returns it, so there is
	 * nothing to poll for in the normal case. Polling stays as the fallback for a
	 * response that came back unusable — the guess may still land from elsewhere.
	 */
	const handleSendThin = async (text: string) => {
		thinHintRef.current = text;
		addUser(text);
		setInputMode("hidden");
		setIsTyping(true);

		let icp: IcpInfo | null = null;

		try {
			const generated = await submitHint({ message: text }).unwrap();

			icp = generated.status === "READY" ? generated : null;
		} catch (err) {
			console.error(err);
		}

		if (!icp) {
			icp = await pollUntilReady(hintWaitMs);
		}

		if (cancelledRef.current) {
			return;
		}

		setIsTyping(false);

		if (icp) {
			confirmStep.showGuess(icp, ts("thinAck"));
		} else {
			addAi(<>{ts("generationTimeout")}</>);
			showBoth();
		}
	};

	const handleSend = () => {
		const text = inputValue.trim();

		if (!text) {
			return;
		}

		setInputValue("");

		if (inputMode === "thin") {
			handleSendThin(text);
		} else if (inputMode === "adjust") {
			void confirmStep.handleSendAdjust(text);
		} else if (inputMode === "customers") {
			void seedsStep.handleSendCustomers(text);
		}
	};

	useEffect(() => {
		if (bootedRef.current) {
			return;
		}

		bootedRef.current = true;
		cancelledRef.current = false;

		const boot = async () => {
			setIsTyping(true);
			const icp = await pollUntilReady(richWaitMs);

			if (cancelledRef.current) {
				return;
			}

			setIsTyping(false);

			if (icp) {
				confirmStep.showGuess(icp, ts("richIntro", { company: companyName }));
			} else {
				addAi(
					<>
						{ts("thinOpener1", { company: companyName })}
						<br />
						<br />
						{ts("thinOpener2")}
						<br />
						<span className="text-xs italic text-text-meta">{ts("thinExample")}</span>
					</>
				);
				setInputMode("thin");
			}
		};

		boot();

		return () => {
			cancelledRef.current = true;
		};
	}, []);

	const placeholder =
		inputMode === "thin"
			? ts("thinPlaceholder")
			: inputMode === "adjust"
				? ts("adjustPlaceholder")
				: inputMode === "customers"
					? ts("customersPlaceholder")
					: "";

	return (
		<ChatShell
			isTyping={isTyping}
			messages={messages}
			placeholder={placeholder}
			scrollRef={scrollRef}
			showInput={inputMode !== "hidden"}
			value={inputValue}
			onChange={setInputValue}
			onSend={handleSend}
		/>
	);
};
